// ============================================================================
// MyInterviews.js
// Standalone page for managing Phase 3 AI Technical Interviews
// Route: /my-interviews
// Features: Stats, filter, search, edit (modal), delete, view results
// ============================================================================

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

const API_BASE = `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/phase3`;

function MyInterviews() {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');

  // ── State
  const [interviews, setInterviews] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [stats, setStats] = useState(null);
  const [actionLoading, setActionLoading] = useState({});
  const [toast, setToast] = useState({ type: '', text: '' });

  // Edit modal
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editInterview, setEditInterview] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [isSavingEdit, setIsSavingEdit] = useState(false);

  // View modal
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [viewInterview, setViewInterview] = useState(null);
  const [isLoadingDetail] = useState(false);

  // ── Auth check
  useEffect(() => {
    if (!token) { navigate('/'); return; }
    fetchInterviews();
    fetchStats();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  // ── Toast auto-dismiss
  useEffect(() => {
    if (toast.text) {
      const t = setTimeout(() => setToast({ type: '', text: '' }), 4000);
      return () => clearTimeout(t);
    }
  }, [toast]);

  // ── Fetch interviews
  const fetchInterviews = async () => {
    try {
      setIsLoading(true);
      const url = statusFilter
        ? `${API_BASE}/interviews?status=${statusFilter}`
        : `${API_BASE}/interviews`;
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const result = await response.json();
        setInterviews(result.data || []);
      } else if (response.status === 401) {
        localStorage.clear(); navigate('/');
      }
    } catch (error) {
      console.error('Error fetching interviews:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // ── Fetch stats
  const fetchStats = async () => {
    try {
      const response = await fetch(`${API_BASE}/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const result = await response.json();
        setStats(result.data || null);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  // ── Filtered interviews (search)
  const filteredInterviews = useMemo(() => {
    if (!searchQuery.trim()) return interviews;
    const q = searchQuery.toLowerCase();
    return interviews.filter(i =>
      (i.candidate_name || '').toLowerCase().includes(q) ||
      (i.candidate_email || '').toLowerCase().includes(q) ||
      (i.role_type || '').toLowerCase().includes(q) ||
      (i.tech_stack || '').toLowerCase().includes(q) ||
      (i.hiring_manager_name || '').toLowerCase().includes(q)
    );
  }, [interviews, searchQuery]);

  // ═══════════════════════════════════════════════
  // ACTIONS
  // ═══════════════════════════════════════════════

  // ── Send Invite
  const handleSendInvite = async (id) => {
    setActionLoading(prev => ({ ...prev, [id]: 'invite' }));
    try {
      const response = await fetch(`${API_BASE}/interview/${id}/send-invite`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}` }
      });
      const result = await response.json();
      if (response.ok) {
        setToast({ type: 'success', text: 'Invite sent to candidate!' });
        fetchInterviews();
      } else {
        setToast({ type: 'error', text: result.detail || 'Failed to send invite' });
      }
    } catch { setToast({ type: 'error', text: 'Connection error' }); }
    finally { setActionLoading(prev => ({ ...prev, [id]: null })); }
  };

  // ── Cancel / Delete
  const handleCancel = async (id) => {
    if (!window.confirm('Cancel this interview? Usage will be refunded.')) return;
    setActionLoading(prev => ({ ...prev, [id]: 'cancel' }));
    try {
      const response = await fetch(`${API_BASE}/interview/${id}/cancel`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setToast({ type: 'success', text: 'Interview cancelled and usage refunded.' });
        fetchInterviews(); fetchStats();
      } else {
        const result = await response.json();
        setToast({ type: 'error', text: result.detail || 'Cancel failed' });
      }
    } catch { setToast({ type: 'error', text: 'Connection error' }); }
    finally { setActionLoading(prev => ({ ...prev, [id]: null })); }
  };

  // ── Process Results (score + generate reports in one call)
  // Replaces separate Score + Generate Reports steps.
  // Falls back gracefully if webhook already processed the interview.
  const handleProcessResults = async (id) => {
    setActionLoading(prev => ({ ...prev, [id]: 'processing' }));
    try {
      const response = await fetch(`${API_BASE}/interview/${id}/score-and-report`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}` }
      });
      const result = await response.json();
      if (response.ok) {
        const score = result.data?.scoring?.overall_score;
        const rec = (result.data?.scoring?.recommendation || '').replace(/_/g, ' ');
        setToast({ type: 'success', text: `✅ Processed! Score: ${score}/400 · ${rec}` });
        fetchInterviews(); fetchStats();
      } else {
        setToast({ type: 'error', text: result.detail || 'Processing failed' });
      }
    } catch { setToast({ type: 'error', text: 'Connection error' }); }
    finally { setActionLoading(prev => ({ ...prev, [id]: null })); }
  };


  // ═══════════════════════════════════════════════
  // EDIT MODAL
  // ═══════════════════════════════════════════════

  const openEditModal = (interview) => {
    const dtStr = interview.scheduled_datetime
      ? String(interview.scheduled_datetime).replace('Z', '').replace('+00:00', '')
      : null;
    const dt = dtStr ? new Date(dtStr) : null;
    setEditInterview(interview);
    setEditForm({
      candidate_name: interview.candidate_name || '',
      candidate_email: interview.candidate_email || '',
      interview_level: interview.interview_level || 'intermediate',
      duration_minutes: interview.duration_minutes || 60,
      scheduled_date: dt ? dt.toISOString().split('T')[0] : '',
      scheduled_time: dt ? dt.toTimeString().slice(0, 5) : '',
    });
    setEditModalOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!editInterview) return;
    setIsSavingEdit(true);

    const payload = {};
    if (editForm.candidate_name !== editInterview.candidate_name)
      payload.candidate_name = editForm.candidate_name;
    if (editForm.candidate_email !== editInterview.candidate_email)
      payload.candidate_email = editForm.candidate_email;
    if (editForm.interview_level !== editInterview.interview_level)
      payload.interview_level = editForm.interview_level;
    if (editForm.duration_minutes !== editInterview.duration_minutes)
      payload.duration_minutes = editForm.duration_minutes;
    if (editForm.scheduled_date && editForm.scheduled_time) {
      payload.scheduled_datetime = `${editForm.scheduled_date}T${editForm.scheduled_time}`;
    }

    if (Object.keys(payload).length === 0) {
      setEditModalOpen(false); setIsSavingEdit(false); return;
    }

    try {
      const response = await fetch(`${API_BASE}/interview/${editInterview.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      const result = await response.json();
      if (response.ok) {
        setToast({ type: 'success', text: 'Interview updated successfully!' });
        setEditModalOpen(false);
        fetchInterviews();
      } else {
        setToast({ type: 'error', text: result.detail || 'Update failed' });
      }
    } catch { setToast({ type: 'error', text: 'Connection error' }); }
    finally { setIsSavingEdit(false); }
  };


  // ═══════════════════════════════════════════════
  // HELPERS
  // ═══════════════════════════════════════════════

  const getStatusConfig = (status) => {
    const s = (status || 'scheduled').toLowerCase();
    const map = {
      scheduled:   { icon: '📅', label: 'Scheduled',   bg: 'bg-blue-100',   text: 'text-blue-800',   border: 'border-blue-200' },
      ready:       { icon: '✅', label: 'Ready',       bg: 'bg-cyan-100',   text: 'text-cyan-800',   border: 'border-cyan-200' },
      in_progress: { icon: '🎙️', label: 'In Progress', bg: 'bg-yellow-100', text: 'text-yellow-800', border: 'border-yellow-200' },
      completed:   { icon: '⏳', label: 'Completed',   bg: 'bg-orange-100', text: 'text-orange-800', border: 'border-orange-200' },
      processed:   { icon: '🏆', label: 'Scored',      bg: 'bg-green-100',  text: 'text-green-800',  border: 'border-green-200' },
      cancelled:   { icon: '❌', label: 'Cancelled',   bg: 'bg-red-100',    text: 'text-red-800',    border: 'border-red-200' },
    };
    return map[s] || map.scheduled;
  };

  const getRecommendationConfig = (rec) => {
    if (!rec) return null;
    const map = {
      EXCEEDS_BAR: { icon: '🌟', label: 'Exceeds Bar', color: 'text-green-700 bg-green-100 border-green-300' },
      MEETS_BAR:   { icon: '✅', label: 'Meets Bar',   color: 'text-blue-700 bg-blue-100 border-blue-300' },
      BORDERLINE:  { icon: '⚠️', label: 'Borderline',  color: 'text-yellow-700 bg-yellow-100 border-yellow-300' },
      BELOW_BAR:   { icon: '❌', label: 'Below Bar',   color: 'text-red-700 bg-red-100 border-red-300' },
    };
    return map[rec] || null;
  };

  const formatDateTime = (dt, timezone) => {
    if (!dt) return { date: '—', time: '' };
    // Backend stores UTC — append Z to ensure JS parses as UTC, then convert to candidate timezone
    const utcStr = String(dt).replace('Z', '').replace('+00:00', '') + 'Z';
    const d = new Date(utcStr);
    const tz = timezone || 'UTC';
    return {
      date: d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: tz }),
      time: d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', timeZone: tz })
    };
  };

  const statusCounts = useMemo(() => {
    const counts = { all: interviews.length };
    interviews.forEach(i => {
      const s = (i.status || '').toLowerCase();
      counts[s] = (counts[s] || 0) + 1;
    });
    return counts;
  }, [interviews]);

  // ═══════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">

      {/* ── Header */}
      <div className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(-1)}
                className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors font-medium"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Back
              </button>
              <div className="h-6 w-px bg-gray-300"></div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">My Interviews</h1>
                <p className="text-xs text-gray-500">Phase 3 · AI Technical Interviews</p>
              </div>
            </div>
            <button
              onClick={() => { fetchInterviews(); fetchStats(); }}
              className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-xl text-sm font-bold text-gray-700 transition-all border border-gray-300"
            >
              ↻ Refresh
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">

        {/* ── Toast */}
        {toast.text && (
          <div className={`rounded-xl p-4 flex items-center gap-3 border-2 shadow-sm animate-pulse ${
            toast.type === 'success'
              ? 'bg-green-50 border-green-400 text-green-700'
              : 'bg-red-50 border-red-400 text-red-700'
          }`}>
            <span className="text-xl">{toast.type === 'success' ? '✅' : '❌'}</span>
            <p className="font-bold text-sm">{toast.text}</p>
          </div>
        )}

        {/* ── Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {[
              { label: 'Total', value: stats.total || 0, icon: '📊', color: 'blue' },
              { label: 'Scheduled', value: stats.scheduled || 0, icon: '📅', color: 'cyan' },
              { label: 'In Progress', value: stats.in_progress || 0, icon: '🎙️', color: 'yellow' },
              { label: 'Completed', value: stats.completed || 0, icon: '⏳', color: 'orange' },
              { label: 'Scored', value: stats.processed || 0, icon: '🏆', color: 'green' },
            ].map(({ label, value, icon, color }) => (
              <div key={label} className={`bg-${color}-50 rounded-xl p-4 border border-${color}-200 hover:shadow-md transition-shadow`}>
                <div className="flex items-center justify-between">
                  <p className={`text-xs text-${color}-600 font-semibold uppercase tracking-wide`}>{label}</p>
                  <span className="text-lg">{icon}</span>
                </div>
                <p className={`text-3xl font-bold text-${color}-900 mt-1`}>{value}</p>
              </div>
            ))}
          </div>
        )}

        {/* ── Filter Bar */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-4">
          <div className="flex flex-col md:flex-row gap-4 items-start md:items-center">
            {/* Search */}
            <div className="relative flex-1">
              <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by candidate, role, tech stack, hiring manager..."
                className="w-full pl-10 pr-4 py-2.5 text-sm border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-400 focus:border-blue-400 bg-gray-50 transition-all"
              />
            </div>
            {/* Status filter pills */}
            <div className="flex gap-1.5 flex-wrap">
              {[
                { key: '', label: 'All', count: statusCounts.all },
                { key: 'scheduled', label: 'Scheduled' },
                { key: 'in_progress', label: 'Active' },
                { key: 'completed', label: 'Completed' },
                { key: 'processed', label: 'Scored' },
                { key: 'cancelled', label: 'Cancelled' },
              ].map(({ key, label, count }) => (
                <button
                  key={key}
                  onClick={() => setStatusFilter(key)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                    statusFilter === key
                      ? 'bg-gray-800 text-white shadow-sm'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {label} {count !== undefined ? `(${count})` : ''}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* ── Interview Table */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
          {isLoading ? (
            <div className="p-16 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent mx-auto"></div>
              <p className="text-gray-600 mt-4 font-medium">Loading interviews...</p>
            </div>
          ) : filteredInterviews.length === 0 ? (
            <div className="p-16 text-center">
              <div className="text-6xl mb-4">📭</div>
              <p className="text-gray-600 text-lg font-medium">
                {searchQuery ? 'No interviews match your search' : 'No interviews found'}
              </p>
              <p className="text-gray-400 text-sm mt-1">
                {searchQuery ? 'Try a different search term' : 'Schedule interviews from the Enterprise tab'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="text-left px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-wide">Candidate</th>
                    <th className="text-left px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-wide">Role / Stack</th>
                    <th className="text-left px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-wide">Level</th>
                    <th className="text-left px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-wide">Scheduled</th>
                    <th className="text-left px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-wide">Status</th>
                    <th className="text-left px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-wide">Score</th>
                    <th className="text-right px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-wide w-44">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredInterviews.map((interview) => {
                    const sc = getStatusConfig(interview.status);
                    const dt = formatDateTime(interview.scheduled_datetime, interview.candidate_timezone);
                    const recConfig = getRecommendationConfig(interview.recommendation);
                    const s = (interview.status || '').toLowerCase();
                    const loading = actionLoading[interview.id];

                    return (
                      <tr key={interview.id} className="hover:bg-blue-50/30 transition-colors group">
                        {/* Candidate */}
                        <td className="px-6 py-4">
                          <p className="font-bold text-gray-900">{interview.candidate_name}</p>
                          <p className="text-xs text-gray-500">{interview.candidate_email}</p>
                          {interview.hiring_manager_name && (
                            <p className="text-xs text-gray-400 mt-0.5">HM: {interview.hiring_manager_name}</p>
                          )}
                        </td>

                        {/* Role / Stack */}
                        <td className="px-6 py-4">
                          <p className="font-medium text-gray-900 text-sm">{interview.role_type || '—'}</p>
                          <p className="text-xs text-gray-500 max-w-xs truncate">{interview.tech_stack || '—'}</p>
                        </td>

                        {/* Level */}
                        <td className="px-6 py-4">
                          <span className="text-sm font-medium text-gray-700 capitalize">
                            {interview.interview_level || '—'}
                          </span>
                        </td>

                        {/* Date/Time */}
                        <td className="px-6 py-4">
                          <p className="text-sm text-gray-700 font-medium">{dt.date}</p>
                          <p className="text-xs text-gray-500">{dt.time}</p>
                        </td>

                        {/* Status */}
                        <td className="px-6 py-4">
                          <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold border ${sc.bg} ${sc.text} ${sc.border}`}>
                            <span>{sc.icon}</span>
                            {sc.label}
                          </span>
                          {interview.invite_status && s === 'scheduled' && (
                            <p className="text-xs text-gray-400 mt-1">
                              Invite: {interview.invite_status}
                            </p>
                          )}
                        </td>

                        {/* Score */}
                        <td className="px-6 py-4">
                          {interview.overall_score ? (
                            <div>
                              <p className="font-bold text-gray-900 text-lg">{interview.overall_score}<span className="text-sm text-gray-400">/400</span></p>
                              {recConfig && (
                                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold border ${recConfig.color}`}>
                                  {recConfig.icon} {recConfig.label}
                                </span>
                              )}
                            </div>
                          ) : (
                            <span className="text-sm text-gray-300">—</span>
                          )}
                        </td>

                        {/* Actions */}
                        <td className="px-6 py-4 align-middle w-24">
                          <div className="flex flex-row gap-2 items-center justify-end">

                            {/* Scheduled: Edit, Resend Invite, Delete */}
                            {s === 'scheduled' && (
                              <>
                                <button onClick={() => openEditModal(interview)} disabled={!!loading}
                                  title="Edit Interview"
                                  className="w-8 h-8 flex items-center justify-center rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100 border border-blue-200 transition-all disabled:opacity-40">
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                                </button>
                                <button onClick={() => handleSendInvite(interview.id)} disabled={!!loading}
                                  title="Resend Invite"
                                  className="w-8 h-8 flex items-center justify-center rounded-lg bg-indigo-50 text-indigo-600 hover:bg-indigo-100 border border-indigo-200 transition-all disabled:opacity-40">
                                  {loading === 'invite'
                                    ? <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/></svg>
                                    : <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                                  }
                                </button>
                                <button onClick={() => handleCancel(interview.id)} disabled={!!loading}
                                  title="Delete Interview"
                                  className="w-8 h-8 flex items-center justify-center rounded-lg bg-red-50 text-red-500 hover:bg-red-100 border border-red-200 transition-all disabled:opacity-40">
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                </button>
                              </>
                            )}

                            {/* Ready: Delete only */}
                            {s === 'ready' && (
                              <button onClick={() => handleCancel(interview.id)} disabled={!!loading}
                                title="Delete Interview"
                                className="w-8 h-8 flex items-center justify-center rounded-lg bg-red-50 text-red-500 hover:bg-red-100 border border-red-200 transition-all disabled:opacity-40">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                              </button>
                            )}

                            {/* In Progress: Live indicator */}
                            {s === 'in_progress' && (
                              <span title="Interview in progress" className="flex items-center gap-1.5 text-xs text-yellow-700 font-semibold px-2.5 py-1.5 bg-yellow-50 rounded-lg border border-yellow-200">
                                <span className="w-2 h-2 rounded-full bg-yellow-500 animate-pulse inline-block"></span>
                                Live
                              </span>
                            )}

                            {/* Completed: Process Results */}
                            {s === 'completed' && (
                              <button onClick={() => handleProcessResults(interview.id)} disabled={!!loading}
                                title="Process Results — score interview and generate reports"
                                className="w-8 h-8 flex items-center justify-center rounded-lg bg-gradient-to-br from-purple-600 to-indigo-600 text-white hover:from-purple-700 hover:to-indigo-700 shadow-sm transition-all disabled:opacity-40">
                                {loading === 'processing'
                                  ? <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/></svg>
                                  : <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
                                }
                              </button>
                            )}

                            {/* Processed/Scored: View Report */}
                            {s === 'processed' && (
                              <button onClick={() => navigate(`/phase3/interview/${interview.id}`)}
                                title="View Report"
                                className="w-8 h-8 flex items-center justify-center rounded-lg bg-green-50 text-green-600 hover:bg-green-100 border border-green-200 transition-all">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                              </button>
                            )}

                            {/* Cancelled: dash */}
                            {s === 'cancelled' && (
                              <span className="text-gray-300 text-sm">—</span>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Row count */}
          {!isLoading && filteredInterviews.length > 0 && (
            <div className="px-6 py-3 bg-gray-50 border-t border-gray-200 text-xs text-gray-500 font-medium">
              Showing {filteredInterviews.length} of {interviews.length} interviews
            </div>
          )}
        </div>
      </div>

      {/* ═══════════════════════════════════════════════ */}
      {/* EDIT MODAL                                      */}
      {/* ═══════════════════════════════════════════════ */}
      {editModalOpen && editInterview && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-bold text-gray-900">Edit Interview</h3>
                  <p className="text-sm text-gray-500">ID: {editInterview.id} · Only scheduled interviews can be edited</p>
                </div>
                <button onClick={() => setEditModalOpen(false)} className="text-gray-400 hover:text-gray-600 transition-colors">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Form */}
            <div className="p-6 space-y-5">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">Candidate Name</label>
                  <input type="text" value={editForm.candidate_name}
                    onChange={(e) => setEditForm(p => ({ ...p, candidate_name: e.target.value }))}
                    className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-400 focus:border-blue-400 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">Candidate Email</label>
                  <input type="email" value={editForm.candidate_email}
                    onChange={(e) => setEditForm(p => ({ ...p, candidate_email: e.target.value }))}
                    className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-400 focus:border-blue-400 text-sm"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">Interview Level</label>
                  <select value={editForm.interview_level}
                    onChange={(e) => setEditForm(p => ({ ...p, interview_level: e.target.value }))}
                    className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-400 focus:border-blue-400 text-sm"
                  >
                    <option value="basic">Basic (0-2 yrs)</option>
                    <option value="intermediate">Intermediate (3-6 yrs)</option>
                    <option value="advanced">Advanced (7+ yrs)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">Duration</label>
                  <select value={editForm.duration_minutes}
                    onChange={(e) => setEditForm(p => ({ ...p, duration_minutes: Number(e.target.value) }))}
                    className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-400 focus:border-blue-400 text-sm"
                  >
                    <option value={45}>45 minutes</option>
                    <option value={60}>60 minutes</option>
                    <option value={75}>75 minutes</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">Date</label>
                  <input type="date" value={editForm.scheduled_date}
                    onChange={(e) => setEditForm(p => ({ ...p, scheduled_date: e.target.value }))}
                    className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-400 focus:border-blue-400 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">Time</label>
                  <input type="time" value={editForm.scheduled_time}
                    onChange={(e) => setEditForm(p => ({ ...p, scheduled_time: e.target.value }))}
                    className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-400 focus:border-blue-400 text-sm"
                  />
                </div>
              </div>

              {/* Read-only fields */}
              <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Cannot be changed</p>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-gray-500">Role:</span>
                    <span className="ml-1 font-medium text-gray-800">{editInterview.role_type || '—'}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Tech:</span>
                    <span className="ml-1 font-medium text-gray-800">{editInterview.tech_stack || '—'}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">HM:</span>
                    <span className="ml-1 font-medium text-gray-800">{editInterview.hiring_manager_name || '—'}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Passcode:</span>
                    <span className="ml-1 font-mono font-bold text-gray-800">{editInterview.passcode || '—'}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button onClick={() => setEditModalOpen(false)}
                className="px-5 py-2.5 text-sm font-bold text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl transition-all">
                Cancel
              </button>
              <button onClick={handleSaveEdit} disabled={isSavingEdit}
                className="px-5 py-2.5 text-sm font-bold text-white bg-blue-600 hover:bg-blue-700 rounded-xl transition-all disabled:opacity-50">
                {isSavingEdit ? '⏳ Saving...' : '✓ Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════ */}
      {/* VIEW DETAIL MODAL                               */}
      {/* ═══════════════════════════════════════════════ */}
      {viewModalOpen && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-bold text-gray-900">Interview Results</h3>
                <button onClick={() => { setViewModalOpen(false); setViewInterview(null); }}
                  className="text-gray-400 hover:text-gray-600 transition-colors">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {isLoadingDetail ? (
              <div className="p-16 text-center">
                <div className="animate-spin rounded-full h-10 w-10 border-4 border-blue-500 border-t-transparent mx-auto"></div>
                <p className="text-gray-500 mt-3 text-sm">Loading details...</p>
              </div>
            ) : viewInterview ? (
              <div className="p-6 space-y-6">
                {/* Candidate Info */}
                <div className="flex items-start justify-between">
                  <div>
                    <h4 className="text-xl font-bold text-gray-900">{viewInterview.candidate_name}</h4>
                    <p className="text-sm text-gray-500">{viewInterview.candidate_email}</p>
                    <p className="text-sm text-gray-500 mt-1">{viewInterview.role_type} · {viewInterview.tech_stack}</p>
                  </div>
                  {viewInterview.overall_score && (
                    <div className="text-right">
                      <p className="text-3xl font-bold text-gray-900">{viewInterview.overall_score}<span className="text-lg text-gray-400">/400</span></p>
                      {(() => {
                        const rc = getRecommendationConfig(viewInterview.recommendation);
                        return rc ? (
                          <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold border ${rc.color} mt-1`}>
                            {rc.icon} {rc.label}
                          </span>
                        ) : null;
                      })()}
                    </div>
                  )}
                </div>

                {/* Details Grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {[
                    { label: 'Level', value: viewInterview.interview_level },
                    { label: 'Duration', value: `${viewInterview.actual_duration_minutes || viewInterview.duration_minutes || 60} min` },
                    { label: 'HM', value: viewInterview.hiring_manager_name || '—' },
                    { label: 'Passcode', value: viewInterview.passcode || '—', mono: true },
                  ].map(({ label, value, mono }) => (
                    <div key={label} className="bg-gray-50 rounded-xl p-3 border border-gray-200">
                      <p className="text-xs text-gray-500 font-semibold uppercase">{label}</p>
                      <p className={`text-sm font-bold text-gray-800 capitalize ${mono ? 'font-mono' : ''}`}>{value}</p>
                    </div>
                  ))}
                </div>

                {/* Percentile */}
                {viewInterview.overall_percentile && (
                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-4 border border-blue-200">
                    <p className="text-sm font-semibold text-blue-800">
                      📊 Percentile: Top {100 - viewInterview.overall_percentile}% of candidates at this level
                    </p>
                  </div>
                )}

                {/* Problem Scores */}
                {viewInterview.problem_scores && viewInterview.problem_scores.length > 0 && (
                  <div>
                    <h5 className="text-sm font-bold text-gray-700 uppercase tracking-wide mb-3">Problem Breakdown</h5>
                    <div className="space-y-3">
                      {viewInterview.problem_scores.map((ps, idx) => (
                        <div key={idx} className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
                          <div className="flex items-center justify-between mb-2">
                            <p className="font-semibold text-gray-900 text-sm">Problem {ps.problem_number || idx + 1}</p>
                            <span className="font-bold text-gray-800">{ps.weighted_score || '—'}/100</span>
                          </div>
                          <div className="grid grid-cols-5 gap-2">
                            {['correctness', 'code_quality', 'communication', 'completeness', 'complexity_understanding'].map(dim => (
                              <div key={dim} className="text-center">
                                <p className="text-xs text-gray-400 capitalize">{dim.replace('_', ' ').slice(0, 8)}</p>
                                <p className="text-sm font-bold text-gray-700">{ps[dim] || '—'}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="p-16 text-center text-gray-500">No details available</div>
            )}

            {/* Footer */}
            <div className="p-6 border-t border-gray-200 flex justify-end">
              <button onClick={() => { setViewModalOpen(false); setViewInterview(null); }}
                className="px-5 py-2.5 text-sm font-bold text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl transition-all">
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default MyInterviews;